import { useObserveVisibility } from 'App/hooks';
import { useLandingVideo } from 'App/Providers/games/LandingVideoProvider';
import { useAnalytics } from 'App/Services/Analytics';

import lpThumbnail from '../../../../assets/lp-thumbnail.png';
import { Phone } from '../../../../components/Phone';
import polyglot from '../../../../localization';
import { LandingAction } from '../LandingAction';

import { GameHighlights } from './GameHighlights';

import './styles.scss';

/**
 * WhyFun component renders a landing section explaining why the game is fun.
 * It shows a phone mockup (with shared video slot), a localized title, and a list
 * of game highlights. At the bottom it includes a call-to-action block that
 * triggers the provided click handler.
 *
 * @param {string} lang — Language key used to localize all section text.
 * @param {boolean} consentBannerReady — Consent banner ready indicator.
 * @param {Function} onBtnClick — Handler for the LandingAction CTA click.
 * @param {string} [videoUrl] — LP video URL, used only if the provider did not expose a why-fun slot ref.
 */

const WhyFun = ({ lang, consentBannerReady, onBtnClick, videoUrl }) => {
    const { whyFunSlotRef } = useLandingVideo() || {};
    const { sendGameDesignEvent, sendGoogleEvent } = useAnalytics();
    const visibleRef = useObserveVisibility(() => {
        sendGameDesignEvent('HP:WhyFunVisible');
        sendGoogleEvent('hp_why_fun_visible');
    });

    return (
        <section className='why-fun'>
            <div className='wrapper'>
                <div className='why-fun__content container'>
                    <div className='why-fun__visual'>
                        <Phone
                            videoSlotRef={whyFunSlotRef}
                            videoUrl={videoUrl}
                            className='why-fun__image'
                            image={lpThumbnail}
                            alt={polyglot.t(`${lang}.whyFun.alt.defaultVideo`)}
                        />
                    </div>

                    <div className='why-fun__wrapper' ref={visibleRef}>
                        <h2 className='why-fun__title'>{polyglot.t(`${lang}.whyFun.title`)}</h2>

                        <GameHighlights lang={lang} />
                    </div>
                </div>
            </div>

            <LandingAction
                lang={lang}
                disable={!consentBannerReady}
                onClick={onBtnClick}
                btnClassName='why-fun__btn'
                textClassName='why-fun__btn-text'
                className='why-fun__action'
                alt={polyglot.t(`${lang}.whyFun.alt.btnPlay`)}>
                <p className='why-fun__action-text'>
                    <span>{polyglot.t(`${lang}.whyFun.subtitle1`)}</span>

                    <span className='why-fun__action-text--mark'>
                        {polyglot.t(`${lang}.whyFun.subtitle2`)}
                    </span>

                    <span>{polyglot.t(`${lang}.whyFun.subtitle3`)}</span>
                </p>
            </LandingAction>
        </section>
    );
};

export default WhyFun;
